/**
 * Purpose: Edit society name, description, city, coordinates, visibility, and append photos (owner / staff / admin).
 * Inputs: open, onClose, society, onSaved(updatedSociety).
 * Outputs: Modal dialog; onSaved after successful PATCH.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { societyService } from '../../services/societyService';
import { useToast } from '../../contexts/ToastContext';
import ImageUpload from '../common/ImageUpload';
import SearchableSelect from '../common/SearchableSelect';
import {
  contactNumbersFromSociety,
  normalizeContactNumbersForApi,
} from '../../utils/societyContactNumbers';

const MAX_CONTACT_ROWS = 20;

function normalizeDescription(value) {
  return (value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const SocietyEditModal = ({ open, onClose, society, onSaved }) => {
  const { showSuccess, showError } = useToast();
  const [saving, setSaving] = useState(false);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [contactRows, setContactRows] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    description: '',
    latitude: '',
    longitude: '',
    is_public: true,
  });

  const resetFromSociety = useCallback(() => {
    if (!society) return;
    setFormData({
      name: society.name || '',
      city: society.city || '',
      description: society.description || '',
      latitude: society.latitude != null && society.latitude !== '' ? String(society.latitude) : '',
      longitude: society.longitude != null && society.longitude !== '' ? String(society.longitude) : '',
      is_public: society.is_public !== false,
    });
    setSelectedImages([]);
    const existing = contactNumbersFromSociety(society);
    setContactRows(
      existing.length > 0
        ? existing.map((c) => ({ label: c.label, phone: c.phone }))
        : [{ label: '', phone: '' }],
    );
  }, [society]);

  const cityOptions = useMemo(() => {
    const list = Array.isArray(cities) ? [...cities] : [];
    if (formData.city && !list.includes(formData.city)) {
      list.unshift(formData.city);
    }
    return list;
  }, [cities, formData.city]);

  useEffect(() => {
    if (!open || !society) return undefined;
    resetFromSociety();
    let cancelled = false;
    setLoadingCities(true);
    societyService
      .getCities({ forRegistration: true })
      .then((res) => {
        if (cancelled) return;
        const raw = res.cities || [];
        setCities(Array.isArray(raw) ? [...raw].sort((a, b) => a.localeCompare(b)) : []);
      })
      .catch(() => {
        if (!cancelled) setCities([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCities(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, society, resetFromSociety]);

  useEffect(() => {
    if (!open) return undefined;
    const onEscape = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [open, onClose, saving]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!society) return;
    const identifier = society.slug || society.id;
    const payload = {
      name: (formData.name || '').trim(),
      city: (formData.city || '').trim(),
      description: normalizeDescription(formData.description),
      is_public: !!formData.is_public,
      contact_numbers: normalizeContactNumbersForApi(contactRows),
    };
    if ((formData.latitude || '').trim() !== '') {
      payload.latitude = formData.latitude;
    } else {
      payload.latitude = null;
    }
    if ((formData.longitude || '').trim() !== '') {
      payload.longitude = formData.longitude;
    } else {
      payload.longitude = null;
    }
    if (selectedImages.length > 0) {
      payload.images = selectedImages;
    }

    try {
      setSaving(true);
      const updated = await societyService.updateSociety(identifier, payload);
      showSuccess('Society updated.');
      if (typeof onSaved === 'function') onSaved(updated);
      onClose();
    } catch (err) {
      showError(err?.message || 'Could not update society.');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !society) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50"
      onClick={saving ? undefined : onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-gray-200"
        onClick={(ev) => ev.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="society-edit-title"
      >
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-start justify-between gap-2">
          <h2 id="society-edit-title" className="text-lg font-semibold text-gray-900">
            Edit society
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-gray-500 hover:text-gray-800 p-1 rounded-md disabled:opacity-50"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Changes apply to this society page. New photos are added to the gallery; existing images stay unless removed in admin tools.
          </p>
          <div>
            <label htmlFor="edit-soc-name" className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <input
              id="edit-soc-name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <div>
            <label htmlFor="edit-soc-city" className="block text-sm font-medium text-gray-700 mb-1">
              City *
            </label>
            {loadingCities ? (
              <p className="text-sm text-gray-500">Loading cities…</p>
            ) : cityOptions.length === 0 ? (
              <input
                id="edit-soc-city"
                name="city"
                type="text"
                required
                value={formData.city}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="City name"
              />
            ) : (
              <SearchableSelect
                id="edit-soc-city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                options={cityOptions}
                searchable
                required
                emptyOption={{ value: '', label: 'Select city' }}
                searchPlaceholder="Search cities…"
              />
            )}
          </div>
          <div>
            <label htmlFor="edit-soc-desc" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="edit-soc-desc"
              name="description"
              rows={5}
              value={formData.description}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="block text-sm font-medium text-gray-700">Important contacts</span>
              <button
                type="button"
                disabled={saving || contactRows.length >= MAX_CONTACT_ROWS}
                onClick={() => setContactRows((prev) => [...prev, { label: '', phone: '' }])}
                className="text-sm text-emerald-700 font-medium hover:text-emerald-800 disabled:opacity-50"
              >
                + Add number
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              e.g. Security, Admin office. Shown below the description with tap-to-call.
            </p>
            <div className="space-y-2">
              {contactRows.map((row, index) => (
                <div key={`contact-row-${index}`} className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={row.label}
                    onChange={(e) => {
                      const value = e.target.value;
                      setContactRows((prev) =>
                        prev.map((r, i) => (i === index ? { ...r, label: value } : r)),
                      );
                    }}
                    placeholder="Label (e.g. Security)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    maxLength={50}
                  />
                  <input
                    type="tel"
                    value={row.phone}
                    onChange={(e) => {
                      const value = e.target.value;
                      setContactRows((prev) =>
                        prev.map((r, i) => (i === index ? { ...r, phone: value } : r)),
                      );
                    }}
                    placeholder="Phone number"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    maxLength={30}
                  />
                  <button
                    type="button"
                    disabled={saving || contactRows.length <= 1}
                    onClick={() =>
                      setContactRows((prev) => prev.filter((_, i) => i !== index))
                    }
                    className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40"
                    aria-label="Remove contact"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="edit-soc-lat" className="block text-sm font-medium text-gray-700 mb-1">
                Latitude (optional)
              </label>
              <input
                id="edit-soc-lat"
                name="latitude"
                type="text"
                inputMode="decimal"
                value={formData.latitude}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div>
              <label htmlFor="edit-soc-lng" className="block text-sm font-medium text-gray-700 mb-1">
                Longitude (optional)
              </label>
              <input
                id="edit-soc-lng"
                name="longitude"
                type="text"
                inputMode="decimal"
                value={formData.longitude}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input name="is_public" type="checkbox" checked={formData.is_public} onChange={handleChange} />
            <span className="text-sm text-gray-800">Publicly listed</span>
          </label>
          <div>
            <span className="block text-sm font-medium text-gray-700 mb-2">Add photos</span>
            <ImageUpload
              selectedImages={selectedImages}
              onImagesChange={setSelectedImages}
              maxImages={10}
              disabled={saving}
              onError={(msg) => {
                if (msg) showError(msg);
              }}
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || loadingCities}
              className="px-4 py-2 rounded-md bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SocietyEditModal;
